import type { DataTableColumn } from "@etudescarre/ui";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useEffect } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type ReportItem = {
  id: string;
  numero: string | null;
  dateReception: string | null;
  dateRedaction: string | null;
  contractCode: string | null;
  noticeCount: number;
  hasPdf: boolean;
};

const columns: DataTableColumn<ReportItem>[] = [
  { id: "numero", header: "N°", accessor: "numero" },
  {
    id: "dateReception",
    header: "Date reception",
    accessor: "dateReception",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  {
    id: "dateRedaction",
    header: "Date redaction",
    accessor: "dateRedaction",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  { id: "contractCode", header: "Contrat", accessor: "contractCode" },
  { id: "noticeCount", header: "Avis", accessor: "noticeCount" },
  {
    id: "pdf",
    header: "PDF",
    accessor: "hasPdf",
    cell: ({ value }) =>
      value ? (
        <FilePdfIcon size={18} className="text-red-600" />
      ) : (
        <span className="text-zinc-300">-</span>
      ),
  },
];

const filters: FilterDef[] = [];

export function ClasseurRapports() {
  const { yardId } = useParams<{ yardId: string }>();
  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const [state, setState] = useClasseurState<ReportItem>();

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET("/api/yards/{yardId}/reports", {
        params: {
          path: { yardId: yardId ?? "" },
          query: { direction: "received" },
        },
      });
      if (data) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  return (
    <Classeur
      title="Classeur rapports d'examen"
      items={state.items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucun rapport d'examen"
      total={state.total}
    />
  );
}
